/*
 * Nav.jsx
 * -------
 * Main navigation bar shown on all authenticated and guest pages.
 * Desktop: horizontal top bar with logo, search, notifications, theme toggle, and user menu.
 * Mobile: top bar for logo and actions, bottom tab bar for main navigation.
 * Consumes AuthContext, ThemeContext, and NotificationContext.
 */

import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { useTheme } from "../App";
import { useNotifications } from "../App";
import NotificationDrawer from "./NotificationDrawer";
import SearchBar from "./SearchBar";

// ============================================
// ICONS
// ============================================
function IconHome({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path
        d="M3 9.5L11 3L19 9.5V19H14V14H8V19H3V9.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        fill={active ? "currentColor" : "none"}
      />
    </svg>
  );
}

function IconList({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M8 6H19M8 11H19M8 16H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="4" cy="6" r="1.5" fill="currentColor"/>
      <circle cx="4" cy="11" r="1.5" fill="currentColor"/>
      <circle cx="4" cy="16" r="1.5" fill="currentColor"/>
    </svg>
  );
}

function IconArchive({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect x="2" y="3" width="18" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" fill={active ? "currentColor" : "none"}/>
      <path d="M4 7V18C4 18.6 4.4 19 5 19H17C17.6 19 18 18.6 18 18V7" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M9 12H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function IconSettings({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle cx="11" cy="11" r="3" stroke="currentColor" strokeWidth="1.5" fill={active ? "currentColor" : "none"}/>
      <path
        d="M11 2V4M11 18V20M2 11H4M18 11H20M4.93 4.93L6.34 6.34M15.66 15.66L17.07 17.07M4.93 17.07L6.34 15.66M15.66 6.34L17.07 4.93"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
      />
    </svg>
  );
}

function IconBell() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path
        d="M11 3C7.69 3 5 5.69 5 9V14L3 16V17H19V16L17 14V9C17 5.69 14.31 3 11 3Z"
        stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"
      />
      <path d="M9 17C9 18.1 9.9 19 11 19C12.1 19 13 18.1 13 17" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );
}

function IconSun() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="4" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M10 2V4M10 16V18M2 10H4M16 10H18M4.34 4.34L5.76 5.76M14.24 14.24L15.66 15.66M4.34 15.66L5.76 14.24M14.24 5.76L15.66 4.34"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
      />
    </svg>
  );
}

function IconMoon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M17 12A8 8 0 018 3C4.13 3 1 6.13 1 10C1 13.87 4.13 17 8 17C11.87 17 15 13.87 15 10C15 9.32 14.9 8.67 14.72 8.05C15.93 9.12 17 10.96 17 12Z"
        stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"
      />
    </svg>
  );
}

// ============================================
// DESKTOP NAV
// ============================================
function DesktopNav({ location, navigate, user, isGuest, logout, theme, toggleTheme, unreadCount, onBellClick, searchOpen, setSearchOpen, getInitials }) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close user menu on outside click
  useEffect(() => {
    function handleClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <nav style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      height: "64px",
      background: "var(--bg-surface)",
      borderBottom: "1px solid var(--border)",
      display: "flex",
      alignItems: "center",
      padding: "0 var(--space-6)",
      gap: "var(--space-4)",
      zIndex: 1000,
      boxShadow: "var(--shadow-sm)"
    }}>

      {/* Logo */}
      <div
        onClick={() => navigate(isGuest ? "/guest" : "/dashboard")}
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 900,
          fontSize: "22px",
          color: "var(--accent-green)",
          cursor: "pointer",
          letterSpacing: "-0.5px",
          flexShrink: 0
        }}
      >
        ✓ Checklist
      </div>

      {/* Desktop nav links */}
      {!isGuest && (
        <div style={{ display: "flex", gap: "var(--space-2)", marginLeft: "var(--space-4)" }}>
          {[
            { label: "Dashboard", path: "/dashboard" },
            { label: "Checklists", path: "/checklists" },
            { label: "Archive", path: "/archive" },
          ].map(({ label, path }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontWeight: location.pathname === path ? 600 : 400,
                fontSize: "15px",
                color: location.pathname === path ? "var(--accent-green)" : "var(--text-secondary)",
                padding: "var(--space-2) var(--space-3)",
                borderRadius: "var(--radius-sm)",
                transition: "color var(--transition-fast), background var(--transition-fast)",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--bg-surface-2)"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Guest nav links */}
      {isGuest && (
        <div style={{ display: "flex", gap: "var(--space-2)", marginLeft: "var(--space-4)" }}>
          <button
            onClick={() => navigate("/checklists")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontWeight: location.pathname === "/checklists" ? 600 : 400,
              fontSize: "15px",
              color: location.pathname === "/checklists" ? "var(--accent-green)" : "var(--text-secondary)",
              padding: "var(--space-2) var(--space-3)",
              borderRadius: "var(--radius-sm)",
            }}
          >
            Checklists
          </button>
        </div>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Search toggle */}
      <button
        onClick={() => setSearchOpen(prev => !prev)}
        style={{
          background: searchOpen ? "var(--bg-surface-2)" : "none",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-sm)",
          padding: "var(--space-2) var(--space-3)",
          cursor: "pointer",
          color: "var(--text-secondary)",
          fontFamily: "var(--font-body)",
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          transition: "background var(--transition-fast)"
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        Search
      </button>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        style={{
          background: "none",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-sm)",
          padding: "var(--space-2)",
          cursor: "pointer",
          color: "var(--text-secondary)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background var(--transition-fast)"
        }}
        onMouseEnter={e => e.currentTarget.style.background = "var(--bg-surface-2)"}
        onMouseLeave={e => e.currentTarget.style.background = "none"}
      >
        {theme === "light" ? <IconMoon /> : <IconSun />}
      </button>

      {/* Notification bell -- authenticated only */}
      {!isGuest && (
        <button
          onClick={onBellClick}
          style={{
            position: "relative",
            background: "none",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: "var(--space-2)",
            cursor: "pointer",
            color: "var(--text-secondary)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background var(--transition-fast)"
          }}
          onMouseEnter={e => e.currentTarget.style.background = "var(--bg-surface-2)"}
          onMouseLeave={e => e.currentTarget.style.background = "none"}
        >
          <IconBell />
          {unreadCount > 0 && (
            <span style={{
              position: "absolute",
              top: "4px",
              right: "4px",
              width: "16px",
              height: "16px",
              background: "var(--accent-gold)",
              borderRadius: "50%",
              fontSize: "10px",
              fontWeight: 700,
              color: "var(--bg-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-body)"
            }}>
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Guest sign in prompt */}
      {isGuest && (
        <button
          onClick={() => window.location.href = "/.auth/login/aad"}
          style={{
            background: "var(--accent-green)",
            border: "none",
            borderRadius: "var(--radius-sm)",
            padding: "var(--space-2) var(--space-4)",
            cursor: "pointer",
            color: "white",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: "14px",
            transition: "opacity var(--transition-fast)"
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
        >
          Sign In
        </button>
      )}

      {/* User menu -- authenticated only */}
      {!isGuest && (
        <div ref={userMenuRef} style={{ position: "relative" }}>
          <button
            onClick={() => setUserMenuOpen(prev => !prev)}
            style={{
              background: "var(--accent-green-muted)",
              border: "none",
              borderRadius: "var(--radius-sm)",
              padding: "var(--space-2) var(--space-3)",
              cursor: "pointer",
              color: "var(--accent-green)",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              transition: "opacity var(--transition-fast)"
            }}
          >
            <div style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              background: "var(--accent-green)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: 700
            }}>
              {getInitials(user?.name)}
            </div>
            {user?.name?.split(" ")[0] || "User"}
          </button>

          {/* Dropdown menu */}
          {userMenuOpen && (
            <div style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              right: 0,
              background: "var(--bg-surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)",
              boxShadow: "var(--shadow-lg)",
              minWidth: "160px",
              overflow: "hidden",
              animation: "fadeIn 150ms ease"
            }}>
              <button
                onClick={() => { navigate("/settings"); setUserMenuOpen(false); }}
                style={{
                  width: "100%",
                  background: "none",
                  border: "none",
                  padding: "var(--space-3) var(--space-4)",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  fontSize: "14px",
                  color: "var(--text-primary)",
                  textAlign: "left",
                  transition: "background var(--transition-fast)"
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bg-surface-2)"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                Settings
              </button>
              <div style={{ height: "1px", background: "var(--border)" }} />
              <button
                onClick={logout}
                style={{
                  width: "100%",
                  background: "none",
                  border: "none",
                  padding: "var(--space-3) var(--space-4)",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  fontSize: "14px",
                  color: "var(--status-overdue)",
                  textAlign: "left",
                  transition: "background var(--transition-fast)"
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bg-surface-2)"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}

// ============================================
// MOBILE BOTTOM TAB BAR
// ============================================
function MobileTabBar({ location, navigate, isGuest }) {
  const tabs = isGuest
    ? [
        { label: "Checklists", path: "/checklists", icon: IconList },
      ]
    : [
        { label: "Home", path: "/dashboard", icon: IconHome },
        { label: "Checklists", path: "/checklists", icon: IconList },
        { label: "Archive", path: "/archive", icon: IconArchive },
        { label: "Settings", path: "/settings", icon: IconSettings },
      ];

  return (
    <nav style={{
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      height: "64px",
      background: "var(--bg-surface)",
      borderTop: "1px solid var(--border)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-around",
      zIndex: 1000,
      boxShadow: "0 -4px 16px rgba(0,0,0,0.06)"
    }}>
      {tabs.map(({ label, path, icon: Icon }) => {
        const active = location.pathname === path;
        return (
          <button
            key={path}
            onClick={() => navigate(path)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
              padding: "var(--space-2)",
              color: active ? "var(--accent-green)" : "var(--text-muted)",
              transition: "color var(--transition-fast)",
              flex: 1
            }}
          >
            <Icon active={active} />
            <span style={{
              fontSize: "11px",
              fontFamily: "var(--font-body)",
              fontWeight: active ? 600 : 400
            }}>
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

// ============================================
// MOBILE TOP BAR
// ============================================
function MobileTopBar({ navigate, isGuest, theme, toggleTheme, unreadCount, onBellClick, searchOpen, setSearchOpen }) {
  return (
    <nav style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      height: "56px",
      background: "var(--bg-surface)",
      borderBottom: "1px solid var(--border)",
      display: "flex",
      alignItems: "center",
      padding: "0 var(--space-4)",
      gap: "var(--space-3)",
      zIndex: 1000,
      boxShadow: "var(--shadow-sm)"
    }}>

      {/* Logo */}
      <div
        onClick={() => navigate(isGuest ? "/guest" : "/dashboard")}
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 900,
          fontSize: "20px",
          color: "var(--accent-green)",
          cursor: "pointer",
          flex: 1
        }}
      >
        ✓ Checklist
      </div>

      {/* Search */}
      <button
        onClick={() => setSearchOpen(prev => !prev)}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--text-secondary)",
          padding: "var(--space-2)",
          display: "flex",
          alignItems: "center"
        }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M14 14L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--text-secondary)",
          padding: "var(--space-2)",
          display: "flex",
          alignItems: "center"
        }}
      >
        {theme === "light" ? <IconMoon /> : <IconSun />}
      </button>

      {/* Bell -- authenticated only */}
      {!isGuest && (
        <button
          onClick={onBellClick}
          style={{
            position: "relative",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--text-secondary)",
            padding: "var(--space-2)",
            display: "flex",
            alignItems: "center"
          }}
        >
          <IconBell />
          {unreadCount > 0 && (
            <span style={{
              position: "absolute",
              top: "4px",
              right: "4px",
              width: "14px",
              height: "14px",
              background: "var(--accent-gold)",
              borderRadius: "50%",
              fontSize: "9px",
              fontWeight: 700,
              color: "var(--bg-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-body)"
            }}>
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Guest sign in */}
      {isGuest && (
        <button
          onClick={() => window.location.href = "/.auth/login/aad"}
          style={{
            background: "var(--accent-green)",
            border: "none",
            borderRadius: "var(--radius-sm)",
            padding: "var(--space-1) var(--space-3)",
            cursor: "pointer",
            color: "white",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: "13px"
          }}
        >
          Sign In
        </button>
      )}
    </nav>
  );
}

// ============================================
// MAIN NAV EXPORT
// ============================================
export default function Nav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isGuest, logout, getInitials } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { unreadCount } = useNotifications();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Track window size for responsive layout
  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close search on route change
  useEffect(() => {
    setSearchOpen(false);
  }, [location.pathname]);

  const navProps = {
    location,
    navigate,
    user,
    isGuest,
    logout,
    theme,
    toggleTheme,
    unreadCount,
    onBellClick: () => setDrawerOpen(true),
    searchOpen,
    setSearchOpen,
    getInitials
  };

  return (
    <>
      {isMobile ? (
        <>
          <MobileTopBar {...navProps} />
          <MobileTabBar location={location} navigate={navigate} isGuest={isGuest} />
        </>
      ) : (
        <DesktopNav {...navProps} />
      )}

      {/* Search bar overlay */}
      {searchOpen && (
        <div style={{
          position: "fixed",
          top: isMobile ? "56px" : "64px",
          left: 0,
          right: 0,
          zIndex: 999,
          padding: "var(--space-3) var(--space-6)",
          background: "var(--bg-surface)",
          borderBottom: "1px solid var(--border)",
          boxShadow: "var(--shadow-md)",
          animation: "fadeIn 150ms ease"
        }}>
          <SearchBar onClose={() => setSearchOpen(false)} />
        </div>
      )}

      {/* Notification drawer */}
      <NotificationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      {/* Page offset for fixed nav */}
      <div style={{ height: isMobile ? "56px" : "64px" }} />
      {isMobile && <div style={{ height: "64px" }} />}
    </>
  );
}